import { useCatalystInsightsList } from '@terminal/react-query-hooks/insights/useCatalystInsights';
import { useEarningsInsightsFilter } from '@terminal/react-query-hooks/insights/useEarningsInsightsFilter';
import { parseEarningsInsights } from '@terminal/react-query-hooks/insights/useEarningsInsightsFilter.utils';
import { useFilteredInsights } from '@terminal/react-query-hooks/insights/useFilteredInsights';
import { useSubscription } from '@terminal/stores/use-subscription/useSubscription';
import { METHOD_SUB_CLS_GROUPING } from '@terminal/widgets/asset-overview-widget/method_sub_cls_grouping';
import { startOfDay, subWeeks } from 'date-fns';
import { useMemo } from 'react';

import { useAssetsTableStore } from '../../useAssetsTableStore';

const EARNINGS_INSIGHT_KEY = 'earnings';
const SCENARIO_INSIGHT_KEY = 'scenario';
const KG_INSIGHT_KEY = 'kg';

export interface UseInsightsCellDataParams {
  entityTag: string;
}

export const useInsightsCellData = ({
  entityTag,
}: UseInsightsCellDataParams) => {
  const isBasicUser = useSubscription(state => state.isBasicUser());
  const insightsFilterState = useAssetsTableStore(
    state => state.insightsFilterState
  );

  const fromDate = useMemo(() => startOfDay(subWeeks(new Date(), 1)), []);

  const { data: earningsInsightsData = [] } = useEarningsInsightsFilter({
    body: { entities: [entityTag], from: fromDate.toISOString() },
    isEnabled: !isBasicUser,
  });
  const { data, isLoading } = useFilteredInsights({ entity: [entityTag] });
  const { data: catalystInsights } = useCatalystInsightsList({
    entities: [entityTag],
    categories: ['company', 'market_wide'],
    from: fromDate.toISOString(),
  });

  const earningsInsight = useMemo(
    () => parseEarningsInsights(earningsInsightsData),
    [earningsInsightsData]
  );

  const filteredScenarioInsights = useMemo(() => {
    const allAssetInsights = data?.data?.result ?? [];
    const scenarioInsightsFilterState =
      insightsFilterState[SCENARIO_INSIGHT_KEY];

    if (
      Object.values(scenarioInsightsFilterState).every(item => item.checked)
    ) {
      return allAssetInsights;
    }

    return allAssetInsights.filter(insight => {
      const analysisType = METHOD_SUB_CLS_GROUPING[insight.metadata.topic];
      return analysisType
        ? scenarioInsightsFilterState.find(item => item.key === analysisType)
            ?.checked
        : false;
    });
  }, [data?.data?.result, insightsFilterState]);

  const filteredCatalystInsights = useMemo(() => {
    const kgInsightsFilterState = insightsFilterState[KG_INSIGHT_KEY];

    return catalystInsights?.filter(catalystInsight => {
      const analysisType = METHOD_SUB_CLS_GROUPING[catalystInsight.category];
      return analysisType
        ? kgInsightsFilterState.find(item => item.key === analysisType)?.checked
        : false;
    });
  }, [catalystInsights, insightsFilterState]);

  const showEarningsInsight = useMemo(() => {
    const kgInsightsFilterState = insightsFilterState[KG_INSIGHT_KEY];
    return kgInsightsFilterState.find(item => item.key === EARNINGS_INSIGHT_KEY)
      ?.checked;
  }, [insightsFilterState]);

  return {
    isLoading,
    filteredScenarioInsights,
    filteredCatalystInsights,
    earningsInsight: showEarningsInsight ? earningsInsight : undefined,
  };
};
